/**
 * /dashboard/qa/test-matrix — brief-3 §9.1.
 *
 * One row per @yopmail test user with persona, kept/skipped/completed
 * card counts, last session info, and two CTAs:
 *
 *   * "Open interview" → routes to /dashboard/ai-playground/interview
 *     with `?asUser=<id>` so the existing Interview page picks the user
 *     up via its TestUserPicker.
 *
 *   * "Run 5-turn eval" → POSTs `/admin/qa/run-eval`. The brief's
 *     §13 cost guardrail says the admin should see an estimated token
 *     cost before kicking off the run; we keep the modal lightweight —
 *     a confirm with the static estimate ("≈ 4–6k Haiku tokens, < $0.01")
 *     and a Cancel/Run pair.
 *
 * Page is gated by `super_admin` via the AdminGuard (in App.tsx) plus a
 * runtime check at mount — non-super_admins land here only via direct URL
 * and we'd rather show a "no access" message than crash on a 403.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2, PlayCircle } from 'lucide-react';
import { useAuthStore } from '@/store/auth-store';
import { apiClient } from '@/services/api-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

interface TestUserRow {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  personaId: string | null;
  personaName: string | null;
  personaCode: string | null;
  keptCount: number;
  skippedCount: number;
  completedCount: number;
  lastSessionAt: string | null;
  lastSessionStatus: string | null;
}

interface RunEvalResponse {
  ok: boolean;
  exitCode: number;
  reportPath: string | null;
  stdoutTail: string;
  stderrTail: string;
  rateLimit: { remaining: number; resetSeconds: number };
}

const TEST_USERS_QUERY_KEY = ['admin', 'qa', 'test-users-with-cards'] as const;

// Static cost estimate for the per-user 5-turn harness (5 user turns × ~600
// agent tokens average plus a Haiku rubric pass at end). Rough but enough
// for the admin to know they aren't kicking off a $5 run.
const ESTIMATED_TOKENS_PER_RUN = '4–6k';
const ESTIMATED_COST_PER_RUN = '< $0.01';

export function QaMatrixPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin)();

  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<{
    userId: string;
    response: RunEvalResponse;
  } | null>(null);

  const { data, isLoading, error } = useQuery<TestUserRow[]>({
    queryKey: TEST_USERS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<TestUserRow[]>('/api/v1/admin/qa/test-users-with-cards');
      if (!res.success || !res.data) {
        throw new Error(res.error?.message ?? 'Failed to load test users');
      }
      return res.data as unknown as TestUserRow[];
    },
    enabled: isSuperAdmin,
    staleTime: 30_000,
  });

  const runEval = useMutation({
    mutationFn: async (targetUserId: string) => {
      const res = await apiClient.post<RunEvalResponse>('/api/v1/admin/qa/run-eval', {
        targetUserId,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error?.message ?? 'Eval run failed');
      }
      return { targetUserId, response: res.data as unknown as RunEvalResponse };
    },
    onSuccess: ({ targetUserId, response }) => {
      setRunResult({ userId: targetUserId, response });
      // Refresh the row's last-session column.
      queryClient.invalidateQueries({ queryKey: TEST_USERS_QUERY_KEY });
    },
  });

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        QA matrix is restricted to super admins.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">QA Test Matrix</h1>
        <p className="text-sm text-muted-foreground">
          Per-persona test users with card progress. Run a 5-turn eval to validate planner + skip
          behavior end-to-end.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {(error as Error).message}
        </div>
      )}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Persona</TableHead>
              <TableHead>Name / Email</TableHead>
              <TableHead className="text-right">Kept</TableHead>
              <TableHead className="text-right">Skipped</TableHead>
              <TableHead className="text-right">Completed</TableHead>
              <TableHead>Last session</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                  No test users found. Run <code>pnpm db:seed:test-users</code> to populate.
                </TableCell>
              </TableRow>
            )}
            {data?.map((row) => {
              const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ') || row.email;
              const isRunning = runEval.isPending && runEval.variables === row.id;
              return (
                <TableRow key={row.id} data-testid={`qa-row-${row.id}`}>
                  <TableCell>
                    <Badge variant="outline">{row.personaCode ?? '—'}</Badge>
                    {row.personaName && (
                      <span className="ml-2 text-xs text-muted-foreground">{row.personaName}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{fullName}</div>
                    <div className="text-xs text-muted-foreground">{row.email}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.keptCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.skippedCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.completedCount}</TableCell>
                  <TableCell>
                    {row.lastSessionAt ? (
                      <div className="flex flex-col">
                        <span className="text-xs">
                          {new Date(row.lastSessionAt).toLocaleString()}
                        </span>
                        <Badge variant="secondary" className="mt-1 w-fit text-[10px]">
                          {row.lastSessionStatus}
                        </Badge>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">never</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          navigate(`/dashboard/ai-playground/interview?asUser=${row.id}`)
                        }
                      >
                        <ExternalLink className="mr-1 h-3.5 w-3.5" />
                        Open interview
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setConfirmingUserId(row.id)}
                        disabled={isRunning || runEval.isPending}
                        className="bg-orb-gold text-white hover:bg-orb-gold/90"
                        data-testid={`run-eval-${row.id}`}
                      >
                        {isRunning ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <PlayCircle className="mr-1 h-3.5 w-3.5" />
                        )}
                        Run 5-turn eval
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Confirm modal — brief §13 cost guardrail. Estimated cost shown
          before the call fires. */}
      {confirmingUserId && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmingUserId(null)}
        >
          <div
            className="max-w-md rounded-lg bg-card p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-lg font-semibold">Run 5-turn eval?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Drives a scripted 5-turn interview against this test user, runs a Haiku-graded rubric,
              and writes an HTML report. Estimated cost:{' '}
              <strong>{ESTIMATED_TOKENS_PER_RUN} tokens</strong> (~{ESTIMATED_COST_PER_RUN}).
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmingUserId(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const id = confirmingUserId;
                  setConfirmingUserId(null);
                  runEval.mutate(id);
                }}
                className="bg-orb-gold text-white hover:bg-orb-gold/90"
              >
                Run eval
              </Button>
            </div>
          </div>
        </div>
      )}

      {runEval.error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          Eval failed: {(runEval.error as Error).message}
        </div>
      )}

      {/* Result panel — sticky-ish footer once a run completes. */}
      {runResult && (
        <div
          data-testid="eval-result"
          className="rounded-md border border-orb-teal/30 bg-orb-cream/40 p-4 text-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <strong>{runResult.response.ok ? '✓ Eval completed' : '✗ Eval failed'}</strong>{' '}
              <span className="text-muted-foreground">exit code {runResult.response.exitCode}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setRunResult(null)}>
              Dismiss
            </Button>
          </div>
          {runResult.response.reportPath && (
            <div className="mt-2 break-all text-xs text-muted-foreground">
              Report: <code>{runResult.response.reportPath}</code>
            </div>
          )}
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              stdout / stderr
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded bg-black/80 p-2 text-[10px] text-green-300">
              {runResult.response.stdoutTail}
              {runResult.response.stderrTail &&
                `\n\n--- stderr ---\n${runResult.response.stderrTail}`}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
